import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { IoClose } from "react-icons/io5";

/**
 * Mobile-first modal that slides up as a bottom sheet. Closes on backdrop tap
 * and Escape. Set `fullscreen` for full-height content (e.g. the comments editor).
 *
 * Props: open, onClose, title, children, footer, fullscreen
 */
const Modal = ({ open, onClose, title, children, footer, fullscreen = false }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-slate-900/40 animate-fade-in" onClick={onClose} />
      <div
        className={clsx(
          "relative z-10 flex w-full flex-col bg-white shadow-2xl animate-sheet-up",
          fullscreen
            ? "h-full"
            : "max-h-[92vh] rounded-t-3xl sm:max-w-md sm:rounded-3xl",
        )}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* grab handle (bottom-sheet affordance) */}
        {!fullscreen && <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-slate-300" />}

        {(title || onClose) && (
          <div className="flex items-center justify-between px-5 pt-3 pb-2">
            <h3 className="text-lg font-bold text-brand-700">{title}</h3>
            <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close">
              <IoClose size={22} />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-2">{children}</div>

        {footer && <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
